import * as moment from 'moment-timezone';

import {
  Component,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import {
  CallbackResult,
  CallbackResultStatus,
  dateStringFromISO8601,
  propExists, timeStringFromISO8601,
} from '../../../../../utils';

import {
  Customer,
  CustomerAlert,
  CustomerDocument,
  CustomerEmergencyContact,
  CustomerLatestCheckIn,
  CustomerMedicalNotes,
  CustomerNote,
  CustomerProficiency,
  CustomerProfileDetails,
  CustomerRelation, getLastActivityText,
  getRelationTypeDisplay,
  IncomingPendingCustomerRelation,
  PendingCustomerRelation,
  PendingRelationStatus,
} from '../../../../../models/customer';
import { ListResponse } from '../../../../../models/list-response';
import { CustomCustomerField, OrganizationConfig } from '../../../../../models/organization-config';

import { AccountService } from '../../../../../services/account/account.service';
import { CustomerService } from '../../../../../services/customer/customer.service';
import { OrganizationConfigService } from '../../../../../services/organization-config/organization-config.service';

import { AddLinkedAccountModalComponent } from './modals/add-linked-account-modal/add-linked-account-modal.component';
import {
  ReviewPendingLinkedAccountModalComponent
} from './modals/review-pending-linked-account-modal/review-pending-linked-account-modal.component';
import {
  ConfirmRemoveLinkedAccountModalComponent
} from './modals/confirm-remove-linked-account-modal/confirm-remove-linked-account-modal.component';
import { AddEditCustomerProficiencyModalComponent } from './modals/add-edit-customer-proficiency-modal/add-edit-customer-proficiency-modal.component';
import { CustomerNoteModalComponent } from './modals/customer-note-modal/customer-note-modal.component';
import { AddCustomerNoteModalComponent } from './modals/add-customer-note-modal/add-customer-note-modal.component';
import { AddCustomerAlertModalComponent } from './modals/add-customer-alert-modal/add-customer-alert-modal.component';
import { InvalidDocument, PotentialDuplicateCustomer, PotentialDuplicateCustomerStatus } from '../../../../../models/inbox';
import { InboxService } from '../../../../../services/inbox/inbox.service';
import {
  ReviewInvalidDocumentModalComponent
} from '../../inbox/inbox-customers/inbox-invalid-documents/modals/review-invalid-document-modal/review-invalid-document-modal.component';
import {
  getCustomerAlertStyle,
  getCustomerFieldLabel,
  getCustomerFieldValue,
  getCustomerNameString,
  getCustomerProfileActionButtonLabel,
  openClimberAppInNewTabAsCustomer,
  reconcileActionButtonOrder,
  reconcileSidebarOrder,
  usesSpecificAllthenticateProducts
} from '../../../../../utils-models';
import {
  ReviewDocumentPendingStaffApprovalModalComponent
} from '../../inbox/inbox-customers/inbox-documents-pending-staff-approval/modals/review-document-pending-staff-approval-modal/review-document-pending-staff-approval-modal.component';
import {
  CustomerEmergencyContactModalComponent
} from './modals/customer-emergency-contact-modal/customer-emergency-contact-modal.component';
import { AddCustomerTagModalComponent } from './modals/add-customer-tag-modal/add-customer-tag-modal.component';
import { EditCustomerTagModalComponent } from './modals/edit-customer-tag-modal/edit-customer-tag-modal.component';
import { CustomerChallenge } from '../../../../../models/challenge';
import { ChallengeService } from '../../../../../services/challenge/challenge.service';
import { CheckInNotifierService } from '../../../../../services/check-in/check-in-notifier.service';
import { AddCustomerToChallengeModalComponent } from './modals/add-customer-to-challenge-modal/add-customer-to-challenge-modal.component';
import { CustomerChallengeModalComponent } from './modals/customer-challenge-modal/customer-challenge-modal.component';
import { SimpleMessageModalComponent } from '../../../../../components/modals/simple-message-modal/simple-message-modal.component';
import {
  OpenDocumentForCustomerModalComponent
} from './modals/open-document-for-customer-modal/open-document-for-customer-modal.component';
import { AssociationMember } from '../../../../../models/association';
import { AssociationService } from '../../../../../services/association/association.service';
import {
  AddCustomerToAssociationModalComponent
} from './modals/add-customer-to-association-modal/add-customer-to-association-modal.component';
import {
  EditAssociationForCustomerModalComponent
} from './modals/edit-association-for-customer-modal/edit-association-for-customer-modal.component';
import { CustomerMedicalNotesModalComponent } from './modals/customer-medical-notes-modal/customer-medical-notes-modal.component';
import {
  AddCustomerMedicalNotesModalComponent
} from './modals/add-customer-medical-notes-modal/add-customer-medical-notes-modal.component';
import {
  ReviewAssociationJoinRequestModalComponent
} from '../../inbox/inbox-associations/inbox-association-join-requests/modals/review-association-join-request-modal/review-association-join-request-modal.component';
import { ManageDocumentModalComponent } from './modals/manage-document-modal/manage-document-modal.component';
import {
  ViewPotentialDuplicateCustomersModalComponent
} from '../../inbox/inbox-customers/inbox-potential-duplicate-customers/modals/view-potential-duplicate-customer-modal/view-potential-duplicate-customer-modal.component';
import { CustomInboxService } from '../../../../../services/custom-inbox/custom-inbox.service';
import { CustomInboxItem } from '../../../../../models/custom-inbox';
import { ViewCustomInboxItemComponent } from '../../inbox/inbox-custom/modals/view-custom-inbox-item/view-custom-inbox-item.component';
import {
  CustomerProfileActionButton,
  CustomerProfileSidebarCard,
  DEFAULT_SIDEBAR_ORDER,
  ProfileField
} from '../../../../../models/organization-config-enums';
import { displayMXMerchantCancelError } from '../../modals/utils';


/**
 * The component representing a customer's profile.
 */
@Component({
  selector: 'app-customer-detail',
  templateUrl: './customer-detail.component.html',
  styleUrls: ['./customer-detail.component.css']
})
export class CustomerDetailComponent implements OnInit, OnDestroy {

  // Sub-list nav items in display order; those that don't fit overflow into
  // the "More" dropdown (`appOverflowMenu`).
  private readonly allNavItems: SubListNavItem[] = [
    { label: 'Check-Ins', routerLink: './check-ins' },
    { label: 'Payments', routerLink: './payments' },
    { label: 'Memberships & Passes', routerLink: './memberships-and-passes' },
    { label: 'Reservations', routerLink: './reservations' },
    { label: 'Purchases', routerLink: './purchases', capitanStaffOnly: true },
    { label: 'Activity', routerLink: './activity-log' },
    { label: 'Credits & Vouchers', routerLink: './credits-and-vouchers' },
    { label: 'Emails', routerLink: './emails' },
  ];

  public get navItems(): SubListNavItem[] {
    const isCapitanStaff = !!this.accountService.getMyAccount()?.is_capitan_staff;
    return this.allNavItems.filter(item => !item.capitanStaffOnly || isCapitanStaff);
  }

  // Maintained by the `appOverflowMenu` directive.
  public visibleNavCount: number = this.navItems.length;

  // Built by `buildActionButtons` once the customer and org config load.
  public actionButtons: ProfileActionButton[] = [];
  public visibleActionButtonCount = 0;

  public customerIdInt: number;
  public customer: Customer;
  public customerProfileDetails: CustomerProfileDetails;
  public orgConfig: OrganizationConfig;
  public customCustomerFields: CustomCustomerField[] = null;
  public emergencyContactsList: ListResponse<CustomerEmergencyContact>;
  public medicalNotesList: ListResponse<CustomerMedicalNotes>;
  public notesList: ListResponse<CustomerNote>;
  public alertsList: ListResponse<CustomerAlert>;
  public proficienciesList: ListResponse<CustomerProficiency>;
  public documentsList: ListResponse<CustomerDocument>;
  public invalidDocInboxItemsList: InvalidDocument[];
  public customerChallengesList: ListResponse<CustomerChallenge>;
  public relationsList: ListResponse<CustomerRelation>;
  public pendingRelationsList: ListResponse<PendingCustomerRelation>;
  public incomingPendingRelationsList: ListResponse<IncomingPendingCustomerRelation>;
  public potentialDuplicatesList: PotentialDuplicateCustomer[] = [];
  public associationsList: ListResponse<AssociationMember>;
  public customInboxItems: CustomInboxItem[];

  // The most recent result returned from a modal.
  public modalResult: CallbackResult = null;

  public allthenticateSuccessMessage: string = null;
  public allthenticateErrorMessage: string = null;
  public loadingSendAllthenticateInvite = false;
  public loadingRemoveAllthenticateAccess = false;

  public loadingCustomer = true;
  public loadingCustomerProfileDetails = true;
  public loadingOrganizationConfig = false;
  public loadingEmergencyContacts = true;
  public loadingMedicalNotes = false;
  public loadingNotes = false;
  public loadingAlerts = false;
  public loadingProficiencies = false;
  public loadingDocuments = false;
  public loadingInvalidDocInboxItems = false;
  public loadingCustomerChallenges = false;
  public loadingRelations = false;
  public loadingPendingRelations = false;
  public loadingIncomingPendingRelations = false;
  public loadingPotentialDuplicates = true;
  public loadingAssociations = false;
  public loadingCustomInboxItems = true;
  public loadingDownloadPrintableCustomerBarcode = false;
  public loadingOpenClimberApp = false;

  // A dict mapping a customer alert ID to a boolean indicating whether that
  // alert is currently being dismissed.
  public loadingDismissAlert: {[key: number]: boolean} = {};

  public customerNotFound = false;
  public serverError: number = null;

  // Make browser history available to the template.
  public history: History;

  // Create instance references to lookup functions and enums so they may be
  // called in the template.
  public getRelationTypeDisplay = getRelationTypeDisplay;
  public PendingRelationStatus = PendingRelationStatus;
  public dateStringFromISO8601 = dateStringFromISO8601;
  public CallbackResultStatus = CallbackResultStatus;
  public getCustomerAlertStyle = getCustomerAlertStyle;
  public CustomerProfileSidebarCard = CustomerProfileSidebarCard;

  public sidebarOrder: CustomerProfileSidebarCard[] = DEFAULT_SIDEBAR_ORDER;

  // Check-in poll interval (PRO-1449). Affordable only because the polled
  // endpoint is a single-row lookup; polling the check-in list this often
  // would not be - check what `getCustomerLatestCheckIn` costs before lowering.
  public readonly CHECK_IN_POLL_INTERVAL_MS = 2 * 1000;

  // Delay before the second post-check-in reload; see `scheduleFollowUpRefresh`.
  public readonly CHECK_IN_FOLLOW_UP_REFRESH_MS = 2 * 1000;

  // Untouched-for-this-long profiles stop polling (PRO-1469). The visibility
  // check is not enough: a maximised profile on an unattended front-desk
  // monitor stays `visible` and would poll all night.
  public readonly CHECK_IN_IDLE_TIMEOUT_MS = 5 * 60 * 1000;

  // `mousemove` fires continuously; only minute-scale staleness matters.
  public readonly INTERACTION_RECORD_THROTTLE_MS = 1000;

  // null: never checked in. undefined: not fetched yet, so the next poll only
  // establishes the baseline and refreshes nothing.
  public latestCheckInTimestamp: string | null | undefined = undefined;

  public checkInPollIntervalHolder: any;

  public followUpRefreshTimeoutHolder: any;

  // Post-check-in reloads must not overlap: each loader is a plain assignment
  // of whatever comes back, so an older response can overwrite a newer one.
  public refreshInFlight = false;

  public checkedInSubscription: Subscription;

  // Bound listener for tab visibility, kept so it can be removed on destroy.
  public visibilityChangeListener: () => void;

  // Seeded at construction: opening a profile is itself an interaction.
  public lastInteractionAt: number = Date.now();

  // One bound listener for every event in INTERACTION_EVENT_NAMES.
  public interactionListener: () => void;

  // Registered on `document` in the CAPTURE phase: `scroll` does not bubble
  // out of nested scrollable elements, and this page has several.
  public readonly INTERACTION_EVENT_NAMES =
    ['mousemove', 'keydown', 'click', 'scroll'];

  // Bumped whenever the displayed customer changes, so a poll response that
  // lands after staff opened another profile can be discarded.
  public checkInPollGeneration = 0;

  // Generation of the poll awaiting a response, or null. A poll of the same
  // generation is skipped, not queued. An OLDER generation never holds the
  // slot: waiting on it would leave a newly opened customer un-baselined.
  public checkInPollInFlightGeneration: number | null = null;

  // A background refresh must not replace the page with an error, but a
  // failure means what is on screen is stale, so it is shown as a floating alert.
  public backgroundRefreshErrorMessage: string = null;

  public readonly BACKGROUND_REFRESH_ERROR_MESSAGE =
    'This customer checked in, but their profile could not be updated. ' +
    'Reload the page to see their current alerts and challenges.';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private customerService: CustomerService,
    private challengeService: ChallengeService,
    private checkInNotifierService: CheckInNotifierService,
    private organizationConfigService: OrganizationConfigService,
    private inboxService: InboxService,
    private associationService: AssociationService,
    private customInboxService: CustomInboxService,
    private modalService: NgbModal,
    private ngZone: NgZone,
  ) { }

  ngOnInit() {
    displayMXMerchantCancelError(this.modalService, this.route, this.router);

    this.route.params.subscribe(routeParams => {
      this.customerNotFound = false;
      this.serverError = null;

      this.customerIdInt = parseInt(routeParams.id, 10);
      if (isNaN(this.customerIdInt)) {
        this.customerNotFound = true;
        return;
      }

      // Make browser history available to the template.
      this.history = history;

      this.loadCustomer(this.customerIdInt);
      this.loadCustomerProfileDetails(this.customerIdInt);
      this.loadOrganizationConfig();
      this.loadEmergencyContacts(this.customerIdInt);
      this.loadMedicalNotes(this.customerIdInt);
      this.loadNotes(this.customerIdInt);
      this.loadAlerts(this.customerIdInt);
      this.loadProficiencies(this.customerIdInt);
      this.loadDocuments(this.customerIdInt);
      this.loadInvalidDocInboxItems(this.customerIdInt);
      this.loadCustomerChallenges(this.customerIdInt);
      this.loadRelations(this.customerIdInt);
      this.loadPendingRelations(this.customerIdInt);
      this.loadIncomingPendingRelations(this.customerIdInt);
      this.loadPotentialDuplicates(this.customerIdInt);
      this.loadAssociations(this.customerIdInt);
      this.loadCustomInboxItems(this.customerIdInt);

      // Back-button and address-bar navigation fire no DOM event, so without
      // this an idle page would leave the new customer un-baselined and absorb
      // a check-in made meanwhile into the baseline (PRO-1469).
      this.lastInteractionAt = Date.now();

      // New poll generation; the first poll only establishes the baseline.
      this.checkInPollGeneration++;
      this.latestCheckInTimestamp = undefined;
      this.pollForNewCheckIn();
    });

    this.loadCustomCustomerFields();

    // Started once rather than per route change: the poll reads whichever
    // customer is on screen (PRO-1449).
    this.checkInPollIntervalHolder = setInterval(
      () => { this.pollForNewCheckIn(); },
      this.CHECK_IN_POLL_INTERVAL_MS
    );

    // A check-in made from this page's own Check-Ins tab is announced at once.
    this.checkedInSubscription = this.checkInNotifierService.customerCheckedIn
      .subscribe(customerIdInt => { this.handleCustomerCheckedIn(customerIdInt); });

    // Hidden tabs are not polled (front desks leave tabs open); poll once on
    // the way back so the tab shows the current state immediately.
    this.visibilityChangeListener = () => {
      if (!this.pageIsVisible()) { return; }

      // Restoring the tab is an interaction and ends an idle period. Not left
      // to the window `focus` event: a browser need not fire one when only the
      // active tab changes (PRO-1469).
      this.recordInteraction();
      this.pollForNewCheckIn();
    };
    document.addEventListener('visibilitychange', this.visibilityChangeListener);

    // Idle detection for pages the browser still reports visible (PRO-1469).
    this.interactionListener = () => { this.recordInteraction(); };

    // Outside the Angular zone and passive: `mousemove`/`scroll` fire
    // continuously and zone.js would run change detection per event. The
    // handler never touches the view; resuming the poll re-enters the zone.
    this.ngZone.runOutsideAngular(() => {
      for (const eventName of this.INTERACTION_EVENT_NAMES) {
        document.addEventListener(
          eventName,
          this.interactionListener,
          { capture: true, passive: true }
        );
      }
      window.addEventListener(
        'focus', this.interactionListener, { passive: true }
      );
    });
  }

  ngOnDestroy() {
    // The notifier is an app-wide singleton: its subscription outlives this
    // component unless dropped here.
    clearInterval(this.checkInPollIntervalHolder);
    clearTimeout(this.followUpRefreshTimeoutHolder);
    if (this.checkedInSubscription) {
      this.checkedInSubscription.unsubscribe();
    }
    if (this.visibilityChangeListener) {
      document.removeEventListener(
        'visibilitychange',
        this.visibilityChangeListener
      );
    }
    if (this.interactionListener) {
      for (const eventName of this.INTERACTION_EVENT_NAMES) {
        document.removeEventListener(
          eventName,
          this.interactionListener,
          true
        );
      }
      window.removeEventListener('focus', this.interactionListener);
    }
  }

  /**
   * Whether this page is visible. Treated as visible when the browser does not
   * say otherwise, so an environment without the Page Visibility API polls.
   */
  public pageIsVisible(): boolean {
    return (document.visibilityState !== 'hidden');
  }

  /**
   * Whether the page has gone untouched long enough to stop polling
   * (PRO-1469). Checked at poll time rather than by stopping the interval, so
   * resuming needs no timer restarted.
   */
  public pageIsIdle(): boolean {
    return (
      (Date.now() - this.lastInteractionAt) >= this.CHECK_IN_IDLE_TIMEOUT_MS
    );
  }

  /**
   * Record a staff interaction and resume polling if the page was idle
   * (PRO-1469). `latestCheckInTimestamp` is left alone while idle, so a
   * check-in made meanwhile still counts as new and raises the banner.
   */
  public recordInteraction(): void {
    const now = Date.now();

    // Throttled against the stored time rather than a separate one, so an idle
    // page - whose stored time is minutes old - can never be throttled out of
    // resuming.
    if ((now - this.lastInteractionAt) < this.INTERACTION_RECORD_THROTTLE_MS) {
      return;
    }

    const wasIdle = this.pageIsIdle();
    this.lastInteractionAt = now;

    // Poll now rather than waiting out the tick. Back inside the Angular zone,
    // since the calling listener is registered outside it (see `ngOnInit`).
    if (wasIdle) {
      this.ngZone.run(() => { this.pollForNewCheckIn(); });
    }
  }

  /**
   * Handle a check-in announced by the Staff Site itself (the buttons on this
   * profile's own Check-Ins tab, PRO-1449). The poll would find it within an
   * interval; this just shows the prize banner straight away. The poll
   * re-baselines on its next run, retrying the reload if this one failed.
   *
   * @param customerIdInt - The API ID of the customer who was checked in.
   */
  public handleCustomerCheckedIn(customerIdInt: number) {
    if (customerIdInt !== this.customerIdInt) { return; }

    this.refreshAfterCheckIn(customerIdInt);
  }

  /**
   * Look up the customer's most recent check-in and, if it is new, reload the
   * profile data a check-in changes (PRO-1449). The first call after the
   * displayed customer changes only establishes the baseline. Stands down for
   * a hidden or idle page (PRO-1469).
   *
   * @return A promise which resolves once the poll has been handled.
   */
  public pollForNewCheckIn(): Promise<void> {
    const customerIdInt = this.customerIdInt;
    const generation = this.checkInPollGeneration;

    if (isNaN(customerIdInt) ||
        this.customerNotFound ||
        this.checkInPollInFlightGeneration === generation ||
        !this.pageIsVisible() ||
        this.pageIsIdle()) {
      return Promise.resolve();
    }

    this.checkInPollInFlightGeneration = generation;

    return this.customerService
      .getCustomerLatestCheckIn(customerIdInt)
      .then((response: CustomerLatestCheckIn) => {
        const latestCheckInTimestamp = response.latest_check_in_timestamp;

        // Ignore a response that arrived after staff moved to a different
        // customer - this is not the customer now being watched.
        if (generation !== this.checkInPollGeneration) { return; }

        const isBaseline = (this.latestCheckInTimestamp === undefined);
        const isNewCheckIn =
          !isBaseline && (latestCheckInTimestamp !== this.latestCheckInTimestamp);

        if (!isNewCheckIn) {
          this.latestCheckInTimestamp = latestCheckInTimestamp;
          return;
        }

        // The baseline moves only once the reload succeeds; advancing it up
        // front would make one failed reload lose this visit's prize banner
        // for good. Returning the promise holds the in-flight slot meanwhile.
        return this.refreshAfterCheckIn(customerIdInt).then(isUpToDate => {
          // Staff may have opened somebody else's profile while the reload ran
          // - in which case this timestamp is not the one being watched now.
          if (isUpToDate && generation === this.checkInPollGeneration) {
            this.latestCheckInTimestamp = latestCheckInTimestamp;
          }
        });
      })
      .catch(() => {
        // A failed poll is deliberately not surfaced: nothing already on the
        // page is wrong because of it, and the next poll picks the check-in up.
      })
      .finally(() => {
        // Only the poll still holding the slot may release it: a superseded
        // poll landing late must not free the slot a newer one is using.
        if (this.checkInPollInFlightGeneration === generation) {
          this.checkInPollInFlightGeneration = null;
        }
      });
  }

  /**
   * Reload the parts of the profile that a check-in changes.
   *
   * @param customerIdInt - The API ID of the customer who checked in.
   * @param isFollowUp - Whether this is the delayed second look scheduled by
   *     an earlier call, which must not schedule one of its own.
   * @return A promise resolving to whether the profile now reflects the
   *     check-in. False means a reload failed, which tells the poll not to
   *     count this check-in as dealt with.
   */
  public refreshAfterCheckIn(customerIdInt: number,
                             isFollowUp = false): Promise<boolean> {
    // A running reload reads everything this one would and schedules its own
    // follow-up; running both risks older responses landing last.
    if (this.refreshInFlight) { return Promise.resolve(false); }

    this.backgroundRefreshErrorMessage = null;
    this.refreshInFlight = true;

    return Promise.all([
      // Challenge progress (and so the "awaiting prize" banner) is advanced
      // as part of completing the check-in.
      this.loadCustomerChallenges(customerIdInt, true),

      // Check-in alerts, last check-in date, remaining guest passes.
      this.loadCustomerProfileDetails(customerIdInt, true),

      // Custom inbox rules run synchronously inside `check_in_customer`, so a
      // resulting item already exists by the time this runs.
      this.loadCustomInboxItems(customerIdInt, true),

      // Association adds are asynchronous; `scheduleFollowUpRefresh` covers
      // the ones that land after this reload.
      this.loadAssociations(customerIdInt, true),

      // Check-in count, home location and entry-pass fields change too. They
      // reach the template through `getCustomerFieldValue`, not by name.
      this.loadCustomer(customerIdInt, true),
    ])
      .then(loaded => loaded.every(didLoad => didLoad))
      .finally(() => {
        this.refreshInFlight = false;

        // Scheduled on the way out so the follow-up cannot start while the
        // reload it follows is still running.
        if (!isFollowUp) {
          this.scheduleFollowUpRefresh(customerIdInt);
        }
      });
  }

  /**
   * Reload the profile once more, shortly after a check-in.
   *
   * `perform_automatic_association_adds_from_check_in` is a Celery task, so an
   * association the check-in adds (and its alerts) can land after the check-in
   * returns, and the poll will not reload again because the check-in timestamp
   * has stopped changing. One extra look, not a retry loop.
   * @param customerIdInt - The API ID of the customer who checked in.
   */
  public scheduleFollowUpRefresh(customerIdInt: number): void {
    // A newer check-in supersedes any follow-up still pending for an older one.
    clearTimeout(this.followUpRefreshTimeoutHolder);

    const generation = this.checkInPollGeneration;

    this.followUpRefreshTimeoutHolder = setTimeout(
      () => {
        // Staff may have opened somebody else's profile in the meantime, in
        // which case this customer's data is no longer what is on screen.
        if (generation !== this.checkInPollGeneration) { return; }

        this.refreshAfterCheckIn(customerIdInt, true);
      },
      this.CHECK_IN_FOLLOW_UP_REFRESH_MS
    );
  }

  /**
   * Build the ordered action buttons for the top of the profile grid. The
   * order comes from the org config, reconciled against the canonical set so
   * it tolerates config drift; buttons that don't fit overflow into "More"
   * (`appOverflowMenu`), so the order controls which stay directly clickable.
   */
  private buildActionButtons(): void {
    if (!this.customer || !this.orgConfig) { return; }

    const order = reconcileActionButtonOrder(
      this.orgConfig.customer_profile_action_button_order
    );

    const customerId = this.customer.id;
    const customerName = this.customer.first_name + ' ' + this.customer.last_name;

    const byKey: Record<CustomerProfileActionButton, ProfileActionButton> = {
      [CustomerProfileActionButton.openSellScreen]: {
        key: CustomerProfileActionButton.openSellScreen,
        label: getCustomerProfileActionButtonLabel(CustomerProfileActionButton.openSellScreen),
        routerLink: '/sale',
        queryParams: { customerId: customerId.toString() },
      },
      [CustomerProfileActionButton.addAlert]: {
        key: CustomerProfileActionButton.addAlert,
        label: getCustomerProfileActionButtonLabel(CustomerProfileActionButton.addAlert),
        onClick: () => this.openAddAlertModal(),
      },
      [CustomerProfileActionButton.editProfile]: {
        key: CustomerProfileActionButton.editProfile,
        label: getCustomerProfileActionButtonLabel(CustomerProfileActionButton.editProfile),
        routerLink: '/customers/' + customerId + '/edit-profile',
      },
      [CustomerProfileActionButton.openClimberApp]: {
        key: CustomerProfileActionButton.openClimberApp,
        label: getCustomerProfileActionButtonLabel(CustomerProfileActionButton.openClimberApp),
        onClick: () => this.openClimberAppAsCustomer(),
        tooltip: 'Open the Climber App homepage, logged in as ' + customerName,
      },
      [CustomerProfileActionButton.useRedemptionCode]: {
        key: CustomerProfileActionButton.useRedemptionCode,
        label: getCustomerProfileActionButtonLabel(CustomerProfileActionButton.useRedemptionCode),
        onClick: () => this.openClimberAppRedeemCodePageAsCustomer(),
      },
    };

    this.actionButtons = order.map(key => byKey[key]);
    // Show all buttons until the directive has had a chance to measure the row;
    // it then trims the count to what fits. Only reset before the first measure
    // (or if the set shrank) so a rebuild after an action doesn't flash.
    if (this.visibleActionButtonCount === 0 ||
        this.visibleActionButtonCount > this.actionButtons.length) {
      this.visibleActionButtonCount = this.actionButtons.length;
    }
  }

  /** trackBy for the action-button rows so a rebuild reuses DOM nodes (keeping
   *  the overflow directive from re-measuring and the layout from flashing). */
  public trackActionButton(index: number, button: ProfileActionButton): string {
    return button.key;
  }

  /**
   * Whether the active route is one of the items overflowed into "More", so
   * the "More" trigger can take the active-tab styling.
   */
  public isMoreActive(): boolean {
    if (this.visibleNavCount >= this.navItems.length) { return false; }
    const url = this.router.url;
    for (let i = this.visibleNavCount; i < this.navItems.length; i++) {
      // routerLinks are relative ("./foo"). A sub-list may have child routes
      // (Reservations), so its segment need not end the URL.
      const path = this.navItems[i].routerLink.replace(/^\.\//, '');
      if (url.endsWith('/' + path) ||
          url.includes('/' + path + '?') ||
          url.includes('/' + path + '/')) {
        return true;
      }
    }
    return false;
  }

  /**
   * Load the customer with the given ID.
   * @param customerIdInt - The API ID of the customer.
   * @param isBackgroundRefresh - Whether this is a reload the user did not ask
   *   for; see `loadCustomerProfileDetails` for what that changes.
   * @return A promise resolving to whether the customer was loaded.
   */
  public loadCustomer(customerIdInt: number,
                      isBackgroundRefresh = false): Promise<boolean> {
    if (!isBackgroundRefresh) {
      this.loadingCustomer = true;
    }

    return this.customerService
      .getCustomer(customerIdInt)
      .then(customer => {
        this.customer = customer;
        this.buildActionButtons();
        return true;
      })
      .catch(reason => {
        if (isBackgroundRefresh) {
          this.backgroundRefreshErrorMessage = this.BACKGROUND_REFRESH_ERROR_MESSAGE;
        } else if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.customerNotFound = true;
        } else {
          this.serverError = reason.status;
        }
        return false;
      })
      .finally(() => {
        if (!isBackgroundRefresh) {
          this.loadingCustomer = false;
        }
      });
  }

  /**
   * Load the profile details of the customer with the given ID.
   * @param customerIdInt - The API ID of the customer.
   * @param isBackgroundRefresh - Whether this is a reload the user did not ask
   *   for (see `refreshAfterCheckIn`). Such a reload neither shows the loading
   *   state - the details already on screen stay readable while it runs - nor
   *   tears the page down on failure, since the page being read is not the
   *   thing that went wrong; it reports itself in a floating alert instead.
   * @return A promise resolving to whether the details were loaded.
   */
  public loadCustomerProfileDetails(customerIdInt: number,
                                    isBackgroundRefresh = false): Promise<boolean> {
    if (!isBackgroundRefresh) {
      this.loadingCustomerProfileDetails = true;
    }

    return this.customerService
      .getCustomerProfileDetails(customerIdInt)
      .then(customerProfileDetails => {
        this.customerProfileDetails = customerProfileDetails;
        return true;
      })
      .catch(reason => {
        if (isBackgroundRefresh) {
          this.backgroundRefreshErrorMessage = this.BACKGROUND_REFRESH_ERROR_MESSAGE;
        } else if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.customerNotFound = true;
        } else {
          this.serverError = reason.status;
        }
        return false;
      })
      .finally(() => {
        if (!isBackgroundRefresh) {
          this.loadingCustomerProfileDetails = false;
        }
      });
  }

  /**
   * Load the organization config from the API.
   */
  public loadOrganizationConfig() {
    // Get the local copy first, then refresh it from the API.
    this.orgConfig = this.organizationConfigService
      .getOrganizationConfig();

    if (this.orgConfig === null) {
      this.loadingOrganizationConfig = true;
    } else {
      this.sidebarOrder = reconcileSidebarOrder(
        this.orgConfig.customer_profile_sidebar_order
      );
      this.buildActionButtons();
    }

    this.organizationConfigService
      .loadOrganizationConfig()
      .then(() => {
        this.orgConfig = this.organizationConfigService
          .getOrganizationConfig();
        this.sidebarOrder = reconcileSidebarOrder(
          this.orgConfig.customer_profile_sidebar_order
        );
        this.buildActionButtons();
        this.loadingOrganizationConfig = false;
      });
  }

  /**
   * Load the emergency contacts for the given customer ID.
   * @param customerIdInt - The API ID of the customer.
   */
  public loadEmergencyContacts(customerIdInt: number) {
    this.loadingEmergencyContacts = true;

    this.customerService
      .getCustomerEmergencyContacts(customerIdInt, 1, 100)
      .then(response => { this.emergencyContactsList = response; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        }
      })
      .finally(() => { this.loadingEmergencyContacts = false; });
  }

  /**
   * Load the medical notes for the given customer ID.
   * @param customerIdInt - The API ID of the customer.
   */
  public loadMedicalNotes(customerIdInt: number) {
    this.loadingMedicalNotes = true;

    this.customerService
      .getCustomerMedicalNotes(customerIdInt, 1, 100)
      .then(response => { this.medicalNotesList = response; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        }
      })
      .finally(() => { this.loadingMedicalNotes = false; });
  }

  /**
   * Load the notes for the given customer ID.
   * @param customerIdInt - The API ID of the customer.
   */
  public loadNotes(customerIdInt: number) {
    this.loadingNotes = true;

    this.customerService
      .getCustomerNotes(customerIdInt, 1, 100)
      .then(response => { this.notesList = response; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        }
      })
      .finally(() => { this.loadingNotes = false; });
  }

  /**
   * Load the alerts for the given customer ID.
   * @param customerIdInt - The API ID of the customer.
   */
  public loadAlerts(customerIdInt: number) {
    this.loadingAlerts = true;

    this.customerService
      .getCustomerAlerts(customerIdInt, 1, 100)
      .then(response => { this.alertsList = response; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        }
      })
      .finally(() => { this.loadingAlerts = false; });
  }

  /**
   * Load the proficiencies for the given customer ID.
   * @param customerIdInt - The API ID of the customer.
   */
  public loadProficiencies(customerIdInt: number) {
    this.loadingProficiencies = true;

    this.customerService
      .getCustomerProficiencies(customerIdInt, 1, 100)
      .then(response => { this.proficienciesList = response; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        }
      })
      .finally(() => { this.loadingProficiencies = false; });
  }

  /**
   * Load the documents for the given customer ID.
   * @param customerIdInt - The API ID of the customer.
   */
  public loadDocuments(customerIdInt: number) {
    this.loadingDocuments = true;

    this.customerService
      .getCustomerDocuments(customerIdInt, 1, 100)
      .then(response => { this.documentsList = response; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        }
      })
      .finally(() => { this.loadingDocuments = false; });
  }

  /**
   * Load the invalid document inbox items for the given customer ID.
   * @param customerIdInt - The API ID of the customer.
   */
  public loadInvalidDocInboxItems(customerIdInt: number) {
    this.loadingInvalidDocInboxItems = true;

    this.inboxService
      .getActiveInvalidDocumentsForCustomer(customerIdInt)
      .then(response => { this.invalidDocInboxItemsList = response; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        }
      })
      .finally(() => { this.loadingInvalidDocInboxItems = false; });
  }

  /**
   * Load the challenges for the given customer ID.
   * @param customerIdInt - The API ID of the customer.
   * @param isBackgroundRefresh - Whether this is a reload the user did not ask
   *   for; see `loadCustomerProfileDetails` for what that changes.
   * @return A promise resolving to whether the challenges were loaded.
   */
  public loadCustomerChallenges(customerIdInt: number,
                                isBackgroundRefresh = false): Promise<boolean> {
    if (!isBackgroundRefresh) {
      this.loadingCustomerChallenges = true;
    }

    return this.challengeService
      .getCustomerChallenges(
        1,
        100,
        {
          customer_id: customerIdInt,
          challenge_status_in: 'ACT'
        }
        )
      .then(response => {
        this.customerChallengesList = response;
        return true;
      })
      .catch(reason => {
        if (isBackgroundRefresh) {
          this.backgroundRefreshErrorMessage = this.BACKGROUND_REFRESH_ERROR_MESSAGE;
        } else if (reason.status === 403) {
          this.accountService.logout();
        }
        return false;
      })
      .finally(() => {
        if (!isBackgroundRefresh) {
          this.loadingCustomerChallenges = false;
        }
      });
  }

  /**
   * Load the relations for the given customer ID.
   * @param customerIdInt - The API ID of the customer.
   */
  public loadRelations(customerIdInt: number) {
    this.loadingRelations = true;

    this.customerService
      .getCustomerRelations(customerIdInt, 1, 100)
      .then(response => { this.relationsList = response; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        }
      })
      .finally(() => { this.loadingRelations = false; });
  }

  /**
   * Load the pending relations for the given customer ID.
   * @param customerIdInt - The API ID of the customer.
   */
  public loadPendingRelations(customerIdInt: number) {
    this.loadingPendingRelations = true;

    this.customerService
      .getPendingCustomerRelations(customerIdInt, 1, 100)
      .then(response => { this.pendingRelationsList = response; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        }
      })
      .finally(() => { this.loadingPendingRelations = false; });
  }

  /**
   * Load the incoming pending relations for the given customer ID.
   * @param customerIdInt - The API ID of the customer.
   */
  public loadIncomingPendingRelations(customerIdInt: number) {
    this.loadingIncomingPendingRelations = true;

    this.customerService
      .getIncomingPendingCustomerRelations(customerIdInt, 1, 100)
      .then(response => { this.incomingPendingRelationsList = response; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        }
      })
      .finally(() => { this.loadingIncomingPendingRelations = false; });
  }

  /**
   * Whether the page is currently waiting for any API responses.
   */
  public isLoading() {
    return this.loadingCustomer;
  }

  public loadingAnyAlertCategory(): boolean {
    return (
      this.loadingCustomerProfileDetails ||
      this.loadingInvalidDocInboxItems ||
      this.loadingDocuments ||
      this.loadingPendingRelations ||
      this.loadingIncomingPendingRelations ||
      this.loadingAlerts ||
      this.loadingCustomerChallenges ||
      this.loadingAssociations ||
      this.loadingPotentialDuplicates
    );
  }

  public customerName() {
    return getCustomerNameString(this.customer);
  }

  /**
   * Get the customer's value for the given profile field.
   */
  public getFieldValue(field: ProfileField) {
    return getCustomerFieldValue(
      field,
      this.customer,
      this.customerProfileDetails,
      this.loadingCustomerProfileDetails
    );
  }

  public getDocumentsPendingStaffReview(): CustomerDocument[] {
    if (!this.documentsList) { return []; }

    return this.documentsList.results.filter((doc) => {
      return doc.is_pending_staff_approval;
    });
  }

  /**
   * Whether a non-zero number of emergency contacts have been loaded.
   */
  public emergencyContactResultsPresent(): boolean {
    return (
      this.emergencyContactsList &&
      this.emergencyContactsList.results &&
      (this.emergencyContactsList.results.length > 0)
    );
  }

  /**
   * Whether a non-zero number of medical notes have been loaded.
   */
  public medicalNotesResultsPresent(): boolean {
    return (
      this.medicalNotesList &&
      this.medicalNotesList.results &&
      (this.medicalNotesList.results.length > 0)
    );
  }

  /**
   * Whether a non-zero number of notes have been loaded.
   */
  public noteResultsPresent(): boolean {
    return (
      this.notesList &&
      this.notesList.results &&
      (this.notesList.results.length > 0)
    );
  }

  /**
   * Whether a non-zero number of alerts have been loaded.
   */
  public alertResultsPresent(): boolean {
    return (
      this.alertsList &&
      this.alertsList.results &&
      (this.alertsList.results.length > 0)
    );
  }

  /**
   * Whether a non-zero number of proficiencies have been loaded.
   */
  public proficiencyResultsPresent(): boolean {
    return (
      this.proficienciesList &&
      this.proficienciesList.results &&
      (this.proficienciesList.results.length > 0)
    );
  }

  /**
   * Whether a non-zero number of documents have been loaded.
   */
  public documentResultsPresent(): boolean {
    return (
      this.documentsList &&
      this.documentsList.results &&
      (this.documentsList.results.length > 0)
    );
  }

  /**
   * Whether a non-zero number of customer challenges have been loaded.
   */
  public customerChallengeResultsPresent(): boolean {
    return (
      this.customerChallengesList &&
      this.customerChallengesList.results &&
      (this.customerChallengesList.results.length > 0)
    );
  }

  /**
   * Whether a non-zero number of associations have been loaded.
   */
  public associationResultsPresent(): boolean {
    return (
      this.associationsList &&
      this.associationsList.results &&
      (this.associationsList.results.length > 0)
    );
  }

  /**
   * Whether a non-zero number of invalid document inbox items have been loaded.
   */
  public invalidDocInboxItemResultsPresent() {
    return this.invalidDocInboxItemsList && (this.invalidDocInboxItemsList.length > 0);
  }

  /**
   * Whether a non-zero number of relations have been loaded.
   */
  public relationResultsPresent(): boolean {
    return (
      this.relationsList &&
      this.relationsList.results &&
      (this.relationsList.results.length > 0)
    );
  }

  /**
   * Whether a non-zero number of pending relations have been loaded.
   */
  public pendingRelationResultsPresent(): boolean {
    return (
      this.pendingRelationsList &&
      this.pendingRelationsList.results &&
      (this.pendingRelationsList.results.length > 0)
    );
  }

  /**
   * Whether a non-zero number of incoming pending relations have been loaded.
   */
  public incomingPendingRelationResultsPresent(): boolean {
    return (
      this.incomingPendingRelationsList &&
      this.incomingPendingRelationsList.results &&
      (this.incomingPendingRelationsList.results.length > 0)
    );
  }

  /**
   * Whether a non-zero number of "linked accounts" items, of any kind, have
   * been loaded.
   */
  public linkedAccountsResultsPresent(): boolean {
    return (
      this.relationResultsPresent() ||
      this.pendingRelationResultsPresent() ||
      this.incomingPendingRelationResultsPresent()
    );
  }

  public getRelationsPendingStaffApproval(): PendingCustomerRelation[] {
    if (this.pendingRelationsList) {
      return this.pendingRelationsList.results.filter(
        (relation) => {
          return relation.status === PendingRelationStatus.pendingStaffApproval;
        }
      );
    } else {
      return [];
    }
  }

  public getIncomingRelationsPendingStaffApproval(): IncomingPendingCustomerRelation[] {
    if (this.incomingPendingRelationsList) {
      return this.incomingPendingRelationsList.results.filter(
        (relation) => {
          return relation.status === PendingRelationStatus.pendingStaffApproval;
        }
      );
    } else {
      return [];
    }
  }

  public usesSpecificAllthenticateProducts(): boolean {
    return usesSpecificAllthenticateProducts(this.orgConfig);
  }

  /**
   * Send this customer an Allthenticate invite by hand.
   */
  public sendAllthenticateInvite() {
    if (this.loadingSendAllthenticateInvite) { return; }

    this.allthenticateSuccessMessage = null;
    this.allthenticateErrorMessage = null;
    this.loadingSendAllthenticateInvite = true;

    this.customerService
      .sendAllthenticateInvite(this.customer.id)
      .then(() => {
        this.allthenticateSuccessMessage = 'Allthenticate invite sent.';
        this.loadCustomer(this.customer.id);
      })
      .catch(reason => {
        // 502 as well as 400: the endpoint calls Allthenticate synchronously,
        // so a rejected or unreachable API answers 502 with a message staff
        // need to read, not a server fault to blank the page for.
        if ((reason.status === 400) || (reason.status === 502)) {
          this.allthenticateErrorMessage = reason.error.useful_error_message;
        } else if (reason.status === 403) {
          this.allthenticateErrorMessage =
            'You do not have permission to send Allthenticate invites.';
        } else if (reason.status === 404) {
          // The customer was removed while this profile was open.
          this.customerNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingSendAllthenticateInvite = false;
      });
  }

  /**
   * Remove this customer from the organization's Allthenticate group.
   */
  public removeAllthenticateAccess() {
    if (this.loadingRemoveAllthenticateAccess) { return; }

    const isConfirmed = confirm(
      'Are you sure you want to remove this customer from Allthenticate? ' +
      'They will no longer be able to open the door.'
    );
    if (!isConfirmed) { return; }

    this.allthenticateSuccessMessage = null;
    this.allthenticateErrorMessage = null;
    this.loadingRemoveAllthenticateAccess = true;

    this.customerService
      .removeAllthenticateAccess(this.customer.id)
      .then(() => {
        this.allthenticateSuccessMessage = 'Customer removed from Allthenticate.';
        this.loadCustomer(this.customer.id);
      })
      .catch(reason => {
        // The endpoint reports both a rejected request (400) and a failed
        // removal (502) with useful_error_message: staff must be able to tell
        // that access was NOT revoked.
        if ((reason.status === 400) || (reason.status === 502)) {
          this.allthenticateErrorMessage = reason.error.useful_error_message;
        } else if (reason.status === 403) {
          this.allthenticateErrorMessage =
            'You do not have permission to remove customers from Allthenticate.';
        } else if (reason.status === 404) {
          // The customer was removed while this profile was open.
          this.customerNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingRemoveAllthenticateAccess = false;
      });
  }

  public dismissAlert(customerAlert: CustomerAlert) {
    this.modalResult = null;

    this.loadingDismissAlert[customerAlert.id] = true;

    this.customerService
      .removeCustomerAlert(customerAlert)
      .then(() => {
        this.loadAlerts(this.customer.id);
        // Profile details need to be loaded if the alert prevented check-in,
        // because check-in alerts are included in the profile details response.
        if (customerAlert.prevents_check_in) {
          this.loadCustomerProfileDetails(this.customer.id);
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          alert('You do not have permission to dismiss customer alerts.');
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingDismissAlert[customerAlert.id] = false;
      });
  }

  /**
   * Open the modal for adding a customer alert.
   */
  public openAddAlertModal() {
    this.modalResult = null;

    const modalRef = this.modalService.open(AddCustomerAlertModalComponent);
    modalRef.componentInstance.customer = this.customer;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadAlerts(this.customer.id);
      this.loadCustomer(this.customer.id);
      this.loadCustomerProfileDetails(this.customer.id);
    };
  }

  /**
   * Open the modal for adding a proficiency to the customer.
   */
  public openAddProficiencyModal() {
    if (!this.accountService.getMyAccount().can_manage_customer_proficiencies) {
      const messageModalRef = this.modalService.open(SimpleMessageModalComponent);
      messageModalRef.componentInstance.headerText = 'Add Customer Proficiency';
      messageModalRef.componentInstance.messageText = 'You do not have permission to add proficiencies to customers.';
      messageModalRef.componentInstance.messageAlertClass = 'alert-danger';
      return;
    }

    const modalRef = this.modalService.open(AddEditCustomerProficiencyModalComponent);
    modalRef.componentInstance.customer = this.customer;
    modalRef.componentInstance.savedCallback = () => {
      this.loadProficiencies(this.customer.id);
      this.loadCustomer(this.customer.id);
      this.loadCustomerProfileDetails(this.customer.id);
    };
  }

  /**
   * Open the modal for editing a customer proficiency.
   */
  public openEditProficiencyModal(customerProficiency: CustomerProficiency) {
    const modalRef = this.modalService.open(AddEditCustomerProficiencyModalComponent);
    modalRef.componentInstance.customer = this.customer;
    modalRef.componentInstance.customerProficiency = customerProficiency;
    modalRef.componentInstance.savedCallback = () => {
      this.loadProficiencies(this.customer.id);
      this.loadCustomer(this.customer.id);
      this.loadCustomerProfileDetails(this.customer.id);
    };
  }

  /**
   * Open the modal for adding medical notes.
   */
  public openAddMedicalNotesModal() {
    this.modalResult = null;

    const modalRef = this.modalService.open(AddCustomerMedicalNotesModalComponent);
    modalRef.componentInstance.customer = this.customer;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadMedicalNotes(this.customer.id);
      this.loadCustomer(this.customer.id);
      this.loadCustomerProfileDetails(this.customer.id);
    };
  }

  /**
   * Open the modal for viewing or editing medical notes.
   */
  public openMedicalNotesModal(notes: CustomerMedicalNotes) {
    // Create a copy of the notes so that the notes' info is only
    // updated in the table after it is saved to the API.
    const notesInput: CustomerMedicalNotes = {...notes};

    this.modalResult = null;

    const modalRef = this.modalService.open(CustomerMedicalNotesModalComponent);
    modalRef.componentInstance.notes = notesInput;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadMedicalNotes(this.customer.id);
      this.loadCustomer(this.customer.id);
      this.loadCustomerProfileDetails(this.customer.id);
    };
  }

  /**
   * Open the modal for adding a customer note.
   */
  public openAddNoteModal() {
    this.modalResult = null;

    const modalRef = this.modalService.open(AddCustomerNoteModalComponent);
    modalRef.componentInstance.customer = this.customer;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadNotes(this.customer.id);
    };
  }

  /**
   * Open the modal for viewing or editing a customer note.
   */
  public openNoteModal(customerNote: CustomerNote) {
    // Create a copy of the note so that the note's info is only
    // updated in the table after it is saved to the API.
    const noteInput: CustomerNote = {...customerNote};

    this.modalResult = null;

    const modalRef = this.modalService.open(CustomerNoteModalComponent);
    modalRef.componentInstance.customerNote = noteInput;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadNotes(this.customer.id);
    };
  }

  /**
   * Open the modal for adding an emergency contact.
   */
  public openAddEmergencyContactModal() {
    this.modalResult = null;

    const modalRef = this.modalService.open(CustomerEmergencyContactModalComponent);
    modalRef.componentInstance.customerId = this.customer.id;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadEmergencyContacts(this.customer.id);
    };
  }

  /**
   * Open the modal for editing an emergency contact.
   */
  public openEditEmergencyContactModal(emergencyContact: CustomerEmergencyContact) {
    // Create a copy of the contact so that the contact's info is only
    // updated in the table after it is saved to the API.
    const emergencyContactInput: CustomerEmergencyContact = {...emergencyContact};

    this.modalResult = null;

    const modalRef = this.modalService.open(CustomerEmergencyContactModalComponent);
    modalRef.componentInstance.customerId = this.customer.id;
    modalRef.componentInstance.emergencyContact = emergencyContactInput;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadEmergencyContacts(this.customer.id);
    };
  }

  /**
   * Open the modal for approving or denying a document pending staff approval.
   */
  public openReviewDocumentPendingStaffApprovalModal(document: CustomerDocument) {
    const modalRef = this.modalService.open(ReviewDocumentPendingStaffApprovalModalComponent);
    modalRef.componentInstance.documentId = document.id;
    modalRef.componentInstance.showHideOption = false;
    modalRef.componentInstance.savedCallback = () => {
      this.loadProficiencies(this.customerIdInt);
      this.loadDocuments(this.customerIdInt);
      // Reload customer profile details in order to update the check-in alerts.
      this.loadCustomerProfileDetails(this.customerIdInt);
    };
  }

  /**
   * Open the modal for adding a linked account to the customer.
   */
  public openAddLinkedAccountModal() {
    const modalRef = this.modalService.open(AddLinkedAccountModalComponent);
    modalRef.componentInstance.customer = this.customer;
    modalRef.componentInstance.savedCallback = () => {
      this.loadRelations(this.customer.id);
    };
  }

  /**
   * Open the modal for reviewing an invalid document.
   */
  public openReviewInvalidDocumentInboxItemModal(invalidDocInboxItem: InvalidDocument) {
    const modalRef = this.modalService.open(ReviewInvalidDocumentModalComponent);
    modalRef.componentInstance.invalidDocument = invalidDocInboxItem;
    modalRef.componentInstance.savedCallback = () => {
      this.loadInvalidDocInboxItems(this.customerIdInt);
    };
  }

  /**
   * Open the modal for reviewing a pending linked account for the customer.
   */
  public openReviewPendingLinkedAccountModal(pendingRelation: PendingCustomerRelation) {
    const modalRef = this.modalService.open(ReviewPendingLinkedAccountModalComponent);
    modalRef.componentInstance.relationRequest = {
      id: pendingRelation.id,
      requesting_customer_id: this.customer.id,
      requesting_customer_email: this.customer.email,
      requesting_customer_first_name: this.customer.first_name,
      requesting_customer_last_name: this.customer.last_name,
      related_customer_id: pendingRelation.related_customer_id,
      related_customer_email: pendingRelation.related_customer_email,
      related_customer_first_name: pendingRelation.related_customer_first_name,
      related_customer_last_name: pendingRelation.related_customer_last_name,
      relation: pendingRelation.relation,
      created_at: pendingRelation.created_at
    };
    modalRef.componentInstance.showHideOption = false;
    modalRef.componentInstance.savedCallback = () => {
      this.loadPendingRelations(this.customer.id);
      this.loadRelations(this.customer.id);
    };
  }

  /**
   * Open the modal for reviewing an incoming pending linked account for the customer.
   */
  public openReviewIncomingPendingLinkedAccountModal(pendingRelation: IncomingPendingCustomerRelation) {
    const modalRef = this.modalService.open(ReviewPendingLinkedAccountModalComponent);
    modalRef.componentInstance.relationRequest = {
      id: pendingRelation.id,
      requesting_customer_id: pendingRelation.requesting_customer_id,
      requesting_customer_email: pendingRelation.requesting_customer_email,
      requesting_customer_first_name: pendingRelation.requesting_customer_first_name,
      requesting_customer_last_name: pendingRelation.requesting_customer_last_name,
      related_customer_id: this.customer.id,
      related_customer_email: this.customer.email,
      related_customer_first_name: this.customer.first_name,
      related_customer_last_name: this.customer.last_name,
      relation: pendingRelation.relation,
      created_at: pendingRelation.created_at
    };
    modalRef.componentInstance.showHideOption = false;
    modalRef.componentInstance.savedCallback = () => {
      this.loadIncomingPendingRelations(this.customer.id);
      this.loadRelations(this.customer.id);
    };
  }

  /**
   * Open the modal for confirming the removal of a linked account for the
   * customer.
   */
  public openConfirmRemoveLinkedAccountModal(relation: CustomerRelation) {
    const modalRef = this.modalService.open(ConfirmRemoveLinkedAccountModalComponent);
    modalRef.componentInstance.relation = relation;
    modalRef.componentInstance.customer = this.customer;
    modalRef.componentInstance.savedCallback = () => {
      this.loadRelations(this.customer.id);
    };
  }

  /**
   * Open the modal for adding a tag to the customer.
   */
  public openAddTagModal() {
    this.modalResult = null;

    const modalRef = this.modalService.open(AddCustomerTagModalComponent);
    modalRef.componentInstance.customerId = this.customer.id;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadCustomer(this.customer.id);
    };
  }

  /**
   * Open the modal for editing a tag.
   */
  public openEditTagModal(tagName: string) {
    this.modalResult = null;

    const modalRef = this.modalService.open(EditCustomerTagModalComponent);
    modalRef.componentInstance.customerId = this.customer.id;
    modalRef.componentInstance.tagName = tagName;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadCustomer(this.customer.id);
    };
  }

  public getProficiencyExpirationDisplay(prof: CustomerProficiency): ExpirationDisplay {
    if (prof.granting_document_is_invalid) {
      return {
        className: 'text-danger',
        displayText: 'Document invalid'
      };
    }
    else if (prof.granting_document_is_pending_staff_approval) {
      return {
        className: 'text-warning',
        displayText: 'Document pending approval'
      };
    }
    else if (prof.granting_document_is_denied_staff_approval) {
      return {
        className: 'text-danger',
        displayText: 'Document denied approval'
      };
    }
    else if (prof.is_expired) {
      return {
        className: 'text-danger',
        displayText: 'Expired'
      };
    }
    else if (prof.expiration_date) {
      return {
        className: 'text-muted',
        displayText: 'Exp ' + dateStringFromISO8601(prof.expiration_date)
      };
    } else {
      return {
        className: 'text-muted',
        displayText: 'Does not expire'
      };
    }
  }

  public getDocumentExpirationDisplay(doc: CustomerDocument): ExpirationDisplay {
    if (doc.is_invalid) {
      return {
        className: 'text-danger',
        displayText: 'Invalid'
      };
    }
    else if (doc.is_pending_staff_approval) {
      return {
        className: 'text-warning',
        displayText: 'Pending approval'
      };
    }
    else if (doc.is_denied_staff_approval) {
      return {
        className: 'text-danger',
        displayText: 'Denied approval'
      };
    }
    else if (doc.is_expired) {
      return {
        className: 'text-danger',
        displayText: 'Expired'
      };
    }
    else if (doc.expiration_date) {
      return {
        className: 'text-muted',
        displayText: 'Exp ' + dateStringFromISO8601(doc.expiration_date)
      };
    } else {
      return {
        className: 'text-muted',
        displayText: 'Does not expire'
      };
    }
  }

  /**
   * Open the modal for adding the customer to a challenge.
   */
  public openAddCustomerToChallengeModal() {
    this.modalResult = null;

    const modalRef = this.modalService.open(AddCustomerToChallengeModalComponent);
    modalRef.componentInstance.customer = this.customer;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadCustomerChallenges(this.customer.id);
    };
  }

  /**
   * Open the modal for viewing or editing a customer challenge.
   */
  public openCustomerChallengeModal(customerChallenge: CustomerChallenge) {
    // Create a copy of the challenge so that the challenge's info is only
    // updated in the table after it is saved to the API.
    const challengeInput: CustomerChallenge = {...customerChallenge};

    this.modalResult = null;

    const modalRef = this.modalService.open(CustomerChallengeModalComponent);
    modalRef.componentInstance.customerChallenge = challengeInput;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadCustomerChallenges(this.customer.id);
    };
  }

  public getChallengesAwaitingPrizes(): CustomerChallenge[] {
    if (!this.customerChallengesList || !this.customerChallengesList.results) {
      return [];
    }

    const challengesAwaitingPrizes = [];
    for (const cc of this.customerChallengesList.results) {
      if (cc.is_complete && cc.challenge_will_prompt_for_prize && !cc.prize_given) {
        challengesAwaitingPrizes.push(cc);
      }
    }

    return challengesAwaitingPrizes;
  }

  public openOpenDocumentForCustomerModal() {
    this.modalResult = null;

    const modalRef = this.modalService.open(OpenDocumentForCustomerModalComponent);
    modalRef.componentInstance.customer = this.customer;
  }

  public openManageDocumentModal(customerDocument: CustomerDocument) {
    this.modalResult = null;

    const modalRef = this.modalService.open(ManageDocumentModalComponent);
    modalRef.componentInstance.documentId = customerDocument.id;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadDocuments(this.customer.id);
    };
  }

  /**
   * Open the modal for adding the customer to an association.
   */
  public openAddCustomerToAssociationModal() {
    this.modalResult = null;

    const modalRef = this.modalService.open(AddCustomerToAssociationModalComponent);
    modalRef.componentInstance.customer = this.customer;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadAssociations(this.customer.id);
    };
  }

  public handleAssociationMemberClick(associationMember: AssociationMember) {
    if (associationMember.has_pending_application) {
      this.reviewAssociationJoinRequest(associationMember);
    } else {
      this.editAssociationForCustomer(associationMember);
    }
  }

  /**
   * Open the modal for editing the customer's membership of an association.
   */
  public editAssociationForCustomer(associationMember: AssociationMember) {
    this.modalResult = null;

    const modalRef = this.modalService.open(EditAssociationForCustomerModalComponent);
    modalRef.componentInstance.associationMember = associationMember;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadAssociations(this.customer.id);
    };
  }

  /**
   * Review the customer's request to join an association.
   */
  public reviewAssociationJoinRequest(associationMember: AssociationMember) {
    this.modalResult = null;

    const modalRef = this.modalService.open(ReviewAssociationJoinRequestModalComponent);
    modalRef.componentInstance.associationId = associationMember.association_id;
    modalRef.componentInstance.associationMemberId = associationMember.id;
    modalRef.componentInstance.showHideOption = false;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadAssociations(this.customer.id);
      this.loadCustomerProfileDetails(this.customer.id);
    };
  }

  public reviewPotentialDuplicate(dup: PotentialDuplicateCustomer) {
    this.modalResult = null;

    const modalRef = this.modalService.open(ViewPotentialDuplicateCustomersModalComponent);
    modalRef.componentInstance.dupId = dup.id;
    modalRef.componentInstance.showHideOption = false;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadPotentialDuplicates(this.customerIdInt);
      // Reload customer profile details in order to update the check-in alerts.
      this.loadCustomerProfileDetails(this.customerIdInt);
    };
    modalRef.componentInstance.thisCustomerId = this.customerIdInt;
  }

  public getAlertableAssociationMembershipsPendingReverification(): AssociationMember[] {
    if (!this.associationsList || !this.associationsList.results) {
      return [];
    }

    return this.associationsList.results.filter(am => {
      return (am.is_pending_reverification && am.association_shows_reverification_alert);
    });
  }

  public getAssociationJoinRequests(): AssociationMember[] {
    if (!this.associationsList || !this.associationsList.results) {
      return [];
    }

    return this.associationsList.results.filter(am => {
      return am.has_pending_application;
    });
  }

  public openClimberAppAsCustomer() {
    if (this.loadingOpenClimberApp || !this.orgConfig) { return; }

    this.loadingOpenClimberApp = true;

    this.customerService
      .getCustomerClimberAppToken(this.customerIdInt)
      .then(token => {
        openClimberAppInNewTabAsCustomer(
          token,
          this.customerIdInt,
          this.orgConfig.climber_app_base_url,
          this.orgConfig.climber_app_base_url
        );
      })
      .catch(reason => {
        if (propExists(reason.status)) {
          this.serverError = reason.status;
        } else {
          const messageModalRef = this.modalService.open(SimpleMessageModalComponent);
          messageModalRef.componentInstance.headerText = 'Request Failed';
          messageModalRef.componentInstance.messageText = 'We were unable to open the Climber App in a new tab. ' +
            'Please ensure your pop-up blocker is disabled for the Capitan Staff Site and try again.';
          messageModalRef.componentInstance.messageAlertClass = 'alert-danger';
        }
      })
      .finally(() => { this.loadingOpenClimberApp = false; });
  }

  public openClimberAppRedeemCodePageAsCustomer() {
    if (this.loadingOpenClimberApp || !this.orgConfig) { return; }

    this.loadingOpenClimberApp = true;

    this.customerService
      .getCustomerClimberAppToken(this.customerIdInt)
      .then(token => {
        openClimberAppInNewTabAsCustomer(
          token,
          this.customerIdInt,
          this.orgConfig.climber_app_base_url,
          this.orgConfig.climber_app_base_url + 'redemption-codes/redeem/'
        );
      })
      .catch(reason => {
        if (propExists(reason.status)) {
          this.serverError = reason.status;
        } else {
          const messageModalRef = this.modalService.open(SimpleMessageModalComponent);
          messageModalRef.componentInstance.headerText = 'Request Failed';
          messageModalRef.componentInstance.messageText = 'We were unable to open the Climber App in a new tab. ' +
            'Please ensure your pop-up blocker is disabled for the Capitan Staff Site and try again.';
          messageModalRef.componentInstance.messageAlertClass = 'alert-danger';
        }
      })
      .finally(() => { this.loadingOpenClimberApp = false; });
  }

  public getAlertDetailText(alert: CustomerAlert) {
    const details = [];
    if (alert.prevents_check_in) {
      details.push('prevents the customer from checking in');
    }
    if (alert.has_alert_icon) {
      details.push('adds an alert icon to the customer')
    }
    if (details.length > 0) {
      return 'This alert ' + details.join(' and ');
    } else {
      return '';
    }
  }

  public getCustomInboxItemsWithAlerts(): CustomInboxItem[] {
    if (this.loadingCustomInboxItems) { return []; }
    if (!propExists(this.customInboxItems)) { return []; }

    return this.customInboxItems.filter(
      cii => cii.inbox_include_customer_profile_alert
    );
  }

  public reviewCustomInboxItem(item: CustomInboxItem) {
    this.modalResult = null;

    const modalRef = this.modalService.open(ViewCustomInboxItemComponent);
    modalRef.componentInstance.itemId = item.id;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadCustomInboxItems(this.customerIdInt);
      // Reload customer profile details in order to update the check-in alerts.
      this.loadCustomerProfileDetails(this.customerIdInt);
    };
  }

  public getThisCustomerFieldLabel(profileField: ProfileField) {
    return getCustomerFieldLabel(
      profileField,
      this.customCustomerFields
    );
  }

  public getLastActivityShort() {
    if (!this.customer) { return ''; }

    return 'Last active ' + moment(this.customer.last_activity_timestamp).fromNow();
  }

  public getLastActivityDetail() {
    if (!this.customer) { return ''; }

    return (
      getLastActivityText(this.customer, true) +
      ' on ' +
      dateStringFromISO8601(this.customer.last_activity_timestamp) +
      ' at ' +
      timeStringFromISO8601(this.customer.last_activity_timestamp)
    );
  }

  private loadPotentialDuplicates(customerIdInt: number) {
    this.loadingPotentialDuplicates = true;

    this.inboxService
      .getPotentialDuplicateCustomers(
        1,
        5,
        {
          status: PotentialDuplicateCustomerStatus.pendingAction,
          customer_id: customerIdInt
        },
        true
      )
      .then(response => { this.potentialDuplicatesList = response.results; })
      .catch()
      .finally(() => { this.loadingPotentialDuplicates = false; });
  }

  /**
   * Load the association memberships for the given customer ID.
   * @param customerIdInt - The API ID of the customer.
   * @param isBackgroundRefresh - Whether this is a reload the user did not ask
   *   for; see `loadCustomerProfileDetails` for what that changes.
   * @return A promise resolving to whether the associations were loaded.
   */
  private loadAssociations(customerIdInt: number,
                           isBackgroundRefresh = false): Promise<boolean> {
    if (!isBackgroundRefresh) {
      this.loadingAssociations = true;
    }

    return this.associationService
      .getAssociationMembers(1, 100, {customer_id: customerIdInt})
      .then(response => {
        this.associationsList = response;
        return true;
      })
      .catch(reason => {
        if (isBackgroundRefresh) {
          this.backgroundRefreshErrorMessage = this.BACKGROUND_REFRESH_ERROR_MESSAGE;
        } else if (reason.status === 403) {
          this.accountService.logout();
        }
        return false;
      })
      .finally(() => {
        if (!isBackgroundRefresh) {
          this.loadingAssociations = false;
        }
      });
  }

  /**
   * Load the unresolved custom inbox items for the given customer ID.
   * @param customerIdInt - The API ID of the customer.
   * @param isBackgroundRefresh - Whether this is a reload the user did not ask
   *   for; see `loadCustomerProfileDetails` for what that changes.
   * @return A promise resolving to whether the items were loaded.
   */
  private loadCustomInboxItems(customerIdInt: number,
                               isBackgroundRefresh = false): Promise<boolean> {
    if (!isBackgroundRefresh) {
      this.loadingCustomInboxItems = true;
    }

    return this.customInboxService
      .getCustomInboxItems(
        1,
        3000,
        {customer_id: customerIdInt, is_resolved: false}
      )
      .then(response => {
        this.customInboxItems = response.results;
        return true;
      })
      .catch(reason => {
        if (isBackgroundRefresh) {
          this.backgroundRefreshErrorMessage = this.BACKGROUND_REFRESH_ERROR_MESSAGE;
        } else if (reason.status === 403) {
          this.accountService.logout();
        }
        return false;
      })
      .finally(() => {
        if (!isBackgroundRefresh) {
          this.loadingCustomInboxItems = false;
        }
      });
  }

  private loadCustomCustomerFields() {
    this.organizationConfigService
      .getCustomCustomerFields(1, 3000)
      .then(response => {
        this.customCustomerFields = response.results;
      });
  }
}

interface ExpirationDisplay {
  className: string;
  displayText: string;
}

interface SubListNavItem {
  label: string;
  routerLink: string;
  capitanStaffOnly?: boolean;
}

/**
 * A single action button shown at the top of the customer profile grid. Each
 * button is either a link (`routerLink`, optionally with `queryParams`) or a
 * click action (`onClick`).
 */
interface ProfileActionButton {
  key: CustomerProfileActionButton;
  label: string;
  routerLink?: string;
  queryParams?: { [key: string]: string };
  onClick?: () => void;
  tooltip?: string;
}
